/*
 * -----------------------------------------------------------------------
 *  <copyright file="tri/display.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import {
    HC_COLORS_STANDARD,
    IOpraChartPlot,
    IOpraChartSeries,
    OpraChartLineStyle,
    OpraChartSeriesType,
    OpraColours,
    OpraMarkerShape,
    OpraRag,
    OpraVerticalAlign,
} from 'interfaces/opra-chart.interface';
import { formatKey } from 'views/components/central-monitoring/panels/panel-display';
import ScatterLineChart from 'views/components/core/charts/scatter-line-chart';

const getTriOperational = ({ getRef, panelState, sites, siteId, size }: any) => {
    const isSmallerWidthItem = (size.width >= 1277 && size.width <= 1540) || size.width >= 1780;

    const tooltipShowSeriesLabel = true;
    const tooltipShowSeriesThreshold = true;
    const getSeries = () => {
        const indexes = [0, 1, 2, 3, 4];
        const colors = [OpraRag.Red, OpraRag.Amber, OpraRag.Green];
        const seriesNames: string[] = [
            'No Movement',
            'No Trend',
            'Falling',
            'Rising',
            `Stable ${panelState.modelConfig.stableValue} ${panelState.modelConfig.stableVariableType}`,
        ];
        const markers: OpraMarkerShape[] = [
            OpraMarkerShape.Square,
            OpraMarkerShape.Diamond,
            OpraMarkerShape.UpsideDownTriangle,
            OpraMarkerShape.Triangle,
            OpraMarkerShape.Circle,
        ];
        const array = [];
        for (let i = 0; i < indexes.length; i++) {
            for (let j = 0; j < colors.length; j++) {
                array.push({
                    name: `${seriesNames[indexes[i]]}`,
                    type: OpraChartSeriesType.Scatter,
                    colorPerPoint: false,
                    hasConfidences: false,
                    style: {
                        color: HC_COLORS_STANDARD[colors[j]],
                        marker: {
                            isHollow: false,
                            shape: markers[indexes[i]],
                            size: 3,
                        },
                    },
                    data: [...(panelState.panelData.panelData[0].points || [])]
                        .filter((plot: any) => plot.trend === indexes[i] && plot.color === colors[j])
                        .map(
                            (plot: any): IOpraChartPlot => ({
                                x: plot.x,
                                y: plot.y,
                                rag: colors[j],
                                site: sites[plot.siteId]?.Fields['Site.name'],
                                score: +plot.score.toFixed(2),
                                selected: !siteId ? undefined : plot.siteId === siteId,
                                hideTooltip: !siteId ? undefined : plot.siteId !== siteId,
                            }),
                        ),
                });
            }
        }
        return array;
    };

    return (
        <ScatterLineChart
            getRef={getRef}
            legendItemWidth={isSmallerWidthItem ? 150 : 190}
            legendWidth={isSmallerWidthItem ? 450 : 590}
            plotTitle={panelState.chartConfig.plotTitle}
            xAxis={{ title: panelState.chartConfig.xTitle, isDateRange: false }}
            yAxisTitle={panelState.chartConfig.yTitle}
            showLegend={panelState.hideLegend ? !panelState.hideLegend : true}
            secondaryKey={{
                text: formatKey(
                    [
                        [
                            {
                                label: panelState.modelConfig.triThreshold2Type,
                                value: panelState.panelData.panelData[0].threshold2,
                                colour: OpraColours.Red,
                            },
                            {
                                colour: OpraColours.Amber,
                                label: panelState.modelConfig.triThreshold1Type,
                                value: panelState.panelData.panelData[0].threshold1,
                            },
                        ],
                    ],
                    true,
                ),
                position: OpraVerticalAlign.bottom,
            }}
            horizontalLines={[
                {
                    name: '',
                    color: HC_COLORS_STANDARD[OpraColours.Amber],
                    style: OpraChartLineStyle.Dashed,
                    y: panelState.panelData.panelData[0].threshold1,
                },
                {
                    name: '',
                    color: HC_COLORS_STANDARD[OpraColours.Red],
                    style: OpraChartLineStyle.Dashed,
                    y: panelState.panelData.panelData[0].threshold2,
                },
            ]}
            series={getSeries()}
            tooltipShowSeriesLabel={tooltipShowSeriesLabel}
            tooltipShowSeriesThreshold={tooltipShowSeriesThreshold}
            isCustomLegendWidth
            hidePlotTitle={panelState.hidePlotTitle}
        />
    );
};

const getTriOperationalTrend = ({ getRef, panelState, sites, siteId: selectedSiteId, previewMode }: any) => {
    const siteIds: any[] = panelState?.panelData
        ? Object.getOwnPropertyNames(panelState.panelData.panelData[0].triTrendSites ?? {})?.map((x: any) => ({
              siteId: x,
              numPoints: panelState.panelData.panelData[0].triTrendSites[x].length,
          })) ?? []
        : [];

    const tooltipShowSeriesLabel = true;
    const tooltipShowSeriesThreshold = true;
    const maxLen: number = Math.max(...siteIds.map((s: any) => s.numPoints));
    const siteId =
        !selectedSiteId && previewMode ? siteIds.filter((s: any) => s.numPoints === maxLen)[0].siteId : selectedSiteId;
    const seriesData: IOpraChartSeries[] = [];

    const siteData = siteId ? panelState?.panelData?.panelData[0]?.triTrendSites[siteId] ?? [] : [];
    const maxSiteX: number = siteData.length ? Math.max(...siteData.map((p: any) => p.x)) : null;

    if (siteId)
        seriesData.push({
            name: sites[siteId]?.Fields['Site.name'],
            showInLegend: true,
            hasConfidences: false,
            type: OpraChartSeriesType.Line,
            style: {
                lineStyle: OpraChartLineStyle.Solid,
                marker: {
                    shape: OpraMarkerShape.Circle,
                    isHollow: false,
                },
                color: HC_COLORS_STANDARD[OpraColours.DarkBlue],
            },
            data: siteData.map(
                (plot: any): IOpraChartPlot => ({
                    x: plot.x,
                    y: plot.y,
                }),
            ),
        });
    seriesData.push({
        name: `Study ${panelState.modelConfig.studyComparison === 'MEDIAN' ? 'Median' : 'Mean'}`,
        showInLegend: true,
        hasConfidences: false,
        type: OpraChartSeriesType.Line,
        style: {
            lineStyle: OpraChartLineStyle.Solid,
            marker: {
                shape: OpraMarkerShape.Circle,
                isHollow: false,
            },
            color: HC_COLORS_STANDARD[OpraColours.LightBlue],
        },
        data: panelState?.panelData
            ? panelState.panelData.panelData[0].triTrendSitesAverage
                  ?.map(
                      (plot: any): IOpraChartPlot => ({
                          x: plot.x,
                          y: plot.y,
                      }),
                  )
                  .filter((plot: IOpraChartPlot) => maxSiteX == null || plot.x <= maxSiteX) ?? []
            : [],
    });

    return (
        <ScatterLineChart
            hidePlotTitle={panelState.hidePlotTitle}
            getRef={getRef}
            plotTitle={panelState.chartConfig.plotTitle}
            xAxis={{ title: panelState.chartConfig.xTitle, isDateRange: false }}
            yAxisTitle={panelState.chartConfig.yTitle}
            showLegend={panelState.hideLegend ? !panelState.hideLegend : true}
            series={seriesData}
            horizontalLines={[
                {
                    name: '',
                    color: HC_COLORS_STANDARD[OpraColours.Amber],
                    style: OpraChartLineStyle.Dashed,
                    y: panelState.panelData.panelData[0].threshold1,
                },
                {
                    name: '',
                    color: HC_COLORS_STANDARD[OpraColours.Red],
                    style: OpraChartLineStyle.Dashed,
                    y: panelState.panelData.panelData[0].threshold2,
                },
            ]}
            secondaryKey={{
                text: formatKey([
                    [
                        {
                            label: panelState.modelConfig.triThreshold2Type,
                            colour: OpraColours.Red,
                            value: panelState.panelData.panelData[0].threshold2,
                        },
                        {
                            label: panelState.modelConfig.triThreshold1Type,
                            colour: OpraColours.Amber,
                            value: panelState.panelData.panelData[0].threshold1,
                        },
                    ],
                ]),
                position: OpraVerticalAlign.bottom,
            }}
            tooltipShowSeriesLabel={tooltipShowSeriesLabel}
            tooltipShowSeriesThreshold={tooltipShowSeriesThreshold}
            isCustomLegendWidth={false}
        />
    );
};

export { getTriOperational, getTriOperationalTrend };
